// Ficha 01a — captura de teléfono (antes de existir la sesión). Look 1:1 con el mock
// `wizard/01-otp-terminos.html`: card ancho con head (eyebrow/título/sub), fila +52 · input ·
// Captura del teléfono con su hint, y el foot Cancelar/Continuar del mock.
// +52 fijo · 10 dígitos · formato XXX-XXX-XXXX.
// Al enviar: POST /wizard/sessions (crea sesión + gate Fiado D27 + OTP) y navega a .../otp.
// Vive en /wizard (index), fuera del WizardShell: el stepper se monta sin sesión, marcando el 01.
import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, X } from 'lucide-react';
import { Button, usePermissions } from '@/kit';
import { wizardErrorMsg } from '../api/wizardError';
import { useCancelSession, useCreateSession, useResumeSession } from '../api/useWizard';
import { useSellerContext } from '../sellerContext';
import { WizardTopbar } from '../components/WizardTopbar';
import { WizardStepper } from '../components/WizardStepper';
import { WizardCard, WizardCardBody, WizardCardHead } from '../components/WizardCard';
import { WizardFootNav } from '../components/WizardFootNav';
import { formatPhone, toPhoneDigits, toPrefillPhone } from '../phoneFormat';
import { rememberLinkedSku } from '../linkedSku';
import { CANCEL_OTHER_STORE_PERMISSION } from '../phoneHolderCancel';
import { CancelOtherStoreSaleDialog } from '../components/CancelOtherStoreSaleDialog';
import {
    OTHER_STORE_SALE_HINT,
    OTHER_STORE_SALE_MESSAGE,
    isSessionNotOpen,
    phoneHolderConflictOf,
    type PhoneHolderConflict,
} from '../phoneHolder';
import {
    ACCOUNT_OPENING_ACTIVE_HERE_ACTION,
    ACCOUNT_OPENING_ACTIVE_HERE_HINT,
    ACCOUNT_OPENING_ACTIVE_HERE_TITLE,
    ACCOUNT_OPENING_CAPTURE_SUB,
    ACCOUNT_OPENING_CAPTURE_TITLE,
    ACCOUNT_OPENING_NO_STORE,
    ACCOUNT_OPENING_START_FAILED,
    ACCOUNT_OPENING_UNSUPPORTED,
    ACCOUNT_OPENING_UNSUPPORTED_CANCEL_REASON,
    hasAccountOpeningMark,
} from '../accountOpening';
import type { CreateSessionRequest } from '../types';

export function PhoneCaptureStep() {
    const navigate = useNavigate();
    const { sellerId, storeId, hasStore } = useSellerContext();
    const createSession = useCreateSession();
    const resumeSession = useResumeSession();
    const cancelSession = useCancelSession();
    // Deep-link "Reanudar" de la bandeja. El teléfono solo se precarga —el código sale cuando el
    // vendedor toca el botón— y el prospecto viaja al silo para que la venta lo cierre al completarse.
    const {
        phone: prefilledPhone,
        prospect: prospectId,
        sku: linkedSku,
        accountOpening,
    } = useSearch({ from: '/wizard' });
    const [digits, setDigits] = useState(() => toPrefillPhone(prefilledPhone ?? ''));
    /** Venta en curso del teléfono enviado: de esta tienda (para continuarla) o de otra sucursal. */
    const [conflict, setConflict] = useState<(PhoneHolderConflict & { phone: string }) | null>(
        null,
    );
    const [cancellingOtherStore, setCancellingOtherStore] = useState(false);
    const canCancelOtherStore = usePermissions().hasPermission(CANCEL_OTHER_STORE_PERMISSION);

    const valid = digits.length === 10 && hasStore;

    const handleSubmit = async (): Promise<void> => {
        if (!hasStore) {
            toast.error(
                'Tu usuario no tiene una tienda asignada. Pide que te asignen el rol de vendedor en una tienda.',
            );
            return;
        }
        if (!valid) {
            toast.error('Revisa el número — son 10 dígitos.');
            return;
        }
        setConflict(null);
        // La apertura nunca reanuda un prospecto ni liga un equipo: el silo rechaza la marca con prospecto.
        const req: CreateSessionRequest = accountOpening
            ? { phone: digits, storeId, sellerId, accountOpening: true }
            : { phone: digits, storeId, sellerId, prospectId };
        try {
            const res = await createSession.mutateAsync(req);
            if (accountOpening && !hasAccountOpeningMark(res)) {
                await cancelUnmarkedSession(res.sessionId);
                return;
            }
            // Los pasos 01-03 no arrastran el `?sku=`: se liga a la sesión para que el 04 lo abra elegido.
            if (linkedSku && !accountOpening) rememberLinkedSku(res.sessionId, linkedSku);
            await navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId: res.sessionId, step: 'otp' },
                search: (prev) => prev,
            });
        } catch (error) {
            // Ya hay una venta en curso con ese teléfono (regla dura: 1 sesión activa por número).
            // Se muestra en la pantalla, no como toast: si es de esta tienda hay que ofrecer entrar a
            // ella, y un toast se va antes de que puedas decidir.
            const holder = phoneHolderConflictOf(error);
            if (holder) {
                setConflict({ ...holder, phone: digits });
                return;
            }
            toast.error(
                wizardErrorMsg(
                    error,
                    accountOpening
                        ? ACCOUNT_OPENING_START_FAILED
                        : 'No se pudo iniciar la venta. Intenta de nuevo.',
                ),
            );
        }
    };

    /** Un back que no conoce la marca creó una venta: se cancela para no dejar el número retenido. */
    const cancelUnmarkedSession = async (sessionId: string): Promise<void> => {
        try {
            await cancelSession.mutateAsync({
                sessionId,
                reason: ACCOUNT_OPENING_UNSUPPORTED_CANCEL_REASON,
            });
        } catch {
            // Si cancelar falla, el barrido la expira; el aviso es el mismo.
        }
        toast.error(ACCOUNT_OPENING_UNSUPPORTED);
    };

    /**
     * Retoma la venta en curso. Cuesta un OTP nuevo: el silo lo deja pendiente y el paso 01 lo vuelve
     * a pedir aunque el cliente ya lo hubiera pasado. Después del código, el silo dice a dónde va.
     */
    const handleResume = async (): Promise<void> => {
        if (conflict?.kind !== 'SAME_STORE') return;
        const { sessionId } = conflict;
        try {
            await resumeSession.mutateAsync(sessionId);
            await navigate({
                to: '/wizard/s/$sessionId/$step',
                params: { sessionId, step: 'otp' },
            });
        } catch (error) {
            // Ya no está abierta (p. ej. otra sucursal tomó al cliente): la venta dice por qué.
            if (isSessionNotOpen(error)) {
                await navigate({ to: '/wizard/s/$sessionId', params: { sessionId } });
                return;
            }
            toast.error('No se pudo retomar la venta. Intenta de nuevo.');
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-muted/40">
            {/* Topbar con la identidad del vendedor: deja claro que la sesión del portal sigue viva
                y que el OTP de abajo es del CLIENTE, no un segundo login del vendedor. */}
            <header className="flex-none border-b border-border bg-card px-5 py-3">
                <WizardTopbar accountOpening={accountOpening}>
                    {/* Sin sesión todavía: el track se monta igual, con el 01 marcado. Arrancar la
                        venta sin ver dónde estás era la diferencia más visible contra el mock. */}
                    <WizardStepper currentSlug="otp" accountOpening={accountOpening} />
                </WizardTopbar>
            </header>

            <div className="flex flex-1 flex-col items-center px-5 pt-6 pb-10">
                <WizardCard>
                    <WizardCardHead
                        eyebrow="Inicio"
                        title={
                            accountOpening
                                ? ACCOUNT_OPENING_CAPTURE_TITLE
                                : 'Empecemos con el teléfono del cliente'
                        }
                        sub={
                            accountOpening
                                ? ACCOUNT_OPENING_CAPTURE_SUB
                                : 'Le llega un código por WhatsApp o SMS.'
                        }
                    />

                    <WizardCardBody className="flex flex-col gap-3">
                        <label
                            htmlFor="wizard-phone"
                            className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                        >
                            Teléfono celular
                        </label>
                        <div className="flex items-stretch gap-2">
                            <div className="flex h-10 w-[70px] flex-none items-center justify-center rounded-md border border-border bg-muted text-[14px] font-semibold">
                                +52
                            </div>
                            {/* `.input-wrap-clear` del mock: la ✕ borra el número completo de un
                                toque, en vez de dejar al vendedor con el backspace. */}
                            <div className="relative flex flex-1 items-stretch">
                                <input
                                    id="wizard-phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    value={formatPhone(digits)}
                                    onChange={(e) => {
                                        setDigits(toPhoneDigits(e.target.value));
                                        setConflict(null);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && valid) void handleSubmit();
                                    }}
                                    placeholder="XXX-XXX-XXXX"
                                    className="h-10 w-full rounded-md border border-border bg-background pl-3 pr-9 text-[15px] tabular-nums tracking-[0.03em] outline-none focus:border-ring"
                                />
                                {digits !== '' && (
                                    <button
                                        type="button"
                                        aria-label="Limpiar el teléfono"
                                        onClick={() => {
                                            setDigits('');
                                            setConflict(null);
                                        }}
                                        className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <X className="size-3.5" aria-hidden />
                                    </button>
                                )}
                            </div>
                        </div>
                        {!hasStore && (
                            <p className="text-[11.5px] text-destructive">
                                {accountOpening
                                    ? ACCOUNT_OPENING_NO_STORE
                                    : 'Tu usuario no tiene una tienda asignada, así que no puedes iniciar una venta.'}
                            </p>
                        )}

                        {conflict?.kind === 'OTHER_STORE' && (
                            <div className="mt-1 rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2.5 text-warning">
                                <p className="text-[12.5px] font-medium">
                                    {OTHER_STORE_SALE_MESSAGE}
                                </p>
                                <p className="mt-0.5 text-[11.5px]">{OTHER_STORE_SALE_HINT}</p>
                                {canCancelOtherStore && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                                setCancellingOtherStore(true);
                                            }}
                                        >
                                            Cancelar la venta de la otra sucursal
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                        {conflict?.kind === 'SAME_STORE' && (
                            <div className="mt-1 rounded-md border border-border bg-muted/50 px-3 py-2.5">
                                <p className="text-[12.5px] font-medium">
                                    {accountOpening
                                        ? ACCOUNT_OPENING_ACTIVE_HERE_TITLE
                                        : 'Ya hay una venta en curso con este número'}
                                </p>
                                {/* Solo se ofrece continuarla, nunca reiniciarla. */}
                                <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                                    {accountOpening
                                        ? ACCOUNT_OPENING_ACTIVE_HERE_HINT
                                        : 'Continúala donde quedó. Le mandamos un código nuevo.'}
                                </p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <Button
                                        size="sm"
                                        isLoading={resumeSession.isPending}
                                        rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                                        onClick={() => void handleResume()}
                                    >
                                        {accountOpening
                                            ? ACCOUNT_OPENING_ACTIVE_HERE_ACTION
                                            : 'Continuar esa venta'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </WizardCardBody>

                    {/* Foot del mock. Una sola acción y un solo botón: el «Enviar código» de al lado
                        del input hacía exactamente lo mismo y sobraba. */}
                    <WizardFootNav
                        back={
                            <Link
                                to="/sales"
                                className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                            >
                                Cancelar
                            </Link>
                        }
                    >
                        <Button
                            disabled={!valid}
                            isLoading={createSession.isPending}
                            rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                            onClick={() => void handleSubmit()}
                        >
                            Continuar
                        </Button>
                    </WizardFootNav>
                </WizardCard>
                {cancellingOtherStore && conflict?.kind === 'OTHER_STORE' && (
                    <CancelOtherStoreSaleDialog
                        phone={conflict.phone}
                        open
                        onClose={() => {
                            setCancellingOtherStore(false);
                        }}
                        onFreed={() => {
                            // El teléfono quedó libre: se abre la venta de una vez.
                            setConflict(null);
                            void handleSubmit();
                        }}
                    />
                )}
            </div>
        </div>
    );
}
